import { toInt32 } from './his-number.utils';
import { isNil, requireNonNil, requireTrue } from './his-value.utils';

type SplitXOptions = {
    /** Bracket pairs to consider when splitting. Respects nesting. */
    brackets?: ( '()' | '[]' | '{}' | '<>' | ( string & {} ) )[];
    /** Quote characters to consider when splitting. */
    quotes?: ( '"' | '\'' | '`' | ( string & {} ) )[];
    /** Escape characters to consider when splitting. */
    escapeChars?: string[];
    /** Whether to trim each segment after splitting. */
    trimSegments?: boolean;
}

/** Common whitespace and invisible characters regex */
const BLANK_CHARS = /^(?:\s|\u200B|\u200C|\u200D|\u2060|\uFEFF)*$/u;

/** Checks if a value is a string. */
export function isString( value: unknown ): value is string {
    return typeof value === 'string';
}

/** Checks if a string is blank (null, undefined, or only whitespace). */
export function isBlank( value: string | null | undefined ): boolean {
    return !value || BLANK_CHARS.test( value );
}

export function trimToNull( value: string | null | undefined ): string | null {
    return value?.trim() || null;
}

export function trimToUndefined( value: string | null | undefined ): string | undefined {
    return value?.trim() || undefined;
}

export function trimToEmpty( value: string | null | undefined ): string {
    return value?.trim() ?? '';
}

/**
 * Removes excess whitespace from the string:
 * - fully strips leading and trailing whitespace
 * - reduces multiple consecutive whitespace characters within the string to a single space
 * @returns The trimmed string with normalized whitespace, or an empty string if the input is null or undefined.
 */
export function trimAll( value: string ): string {
    return trimToEmpty( value ).replace( /\s+/g, ' ' );
}

/**
 * @deprecated Use `String.prototype.replaceAll()` instead, which is natively supported in modern JavaScript environments and provides better performance and readability.
 */
export function replaceAll( originalValue: string, searchValue: string, replaceValue: string ): string {
    return originalValue.split( searchValue ).join( replaceValue );
}

/**
 * Generates a hash code for a given string, emulating Java's String.hashCode() behavior by using a 32-bit signed integer.
 * The hash code is computed using a polynomial rolling hash algorithm with a base of 31.
 */
export function getHashCode( str: string ): string {
    if ( isNil( str ) ) {
        return '0';
    }
    let hash = 0;
    for ( let i = 0; i < str.length; i++ ) {
        const imul = Math.imul( 31, hash );
        const charCodeAt = str.charCodeAt( i );
        hash = imul + charCodeAt;
    }
    const hashCode = toInt32( hash );
    console.debug( 'his-string.utils #hashCode', hashCode );
    return String( hashCode );
}

/** @deprecated no implementation. */
export function removeLast( value: string, end: string ): string {
    if ( value == undefined ) {
        throw new Error( 'Value must not be null or undefined' );
    }
    return value;
}

/** Removes a prefix from a string if present. */
export function removePrefix( value: string, prefix: string ): string {
    requireNonNil( value, 'Value must not be null or undefined' );
    requireNonNil( prefix, 'Prefix must not be null or undefined' );
    return value.startsWith( prefix ) ? value.slice( prefix.length ) : value;
}

/** Removes a suffix from a string if present. */
export function removeSuffix( value: string, suffix: string ): string {
    requireNonNil( value, 'Value must not be null or undefined' );
    requireNonNil( suffix, 'Suffix must not be null or undefined' );
    return value.endsWith( suffix ) ? value.slice( 0, -suffix.length ) : value;
}

/** Adds a prefix to a string if not already present. */
export function ensurePrefix( value: string, prefix: string ): string {
    requireNonNil( value, 'Value must not be null or undefined' );
    requireNonNil( prefix, 'Prefix must not be null or undefined' );
    return value.startsWith( prefix ) ? value : prefix + value;
}

/** Adds a suffix to a string if not already present. */
export function ensureSuffix( value: string, suffix: string ): string {
    requireNonNil( value, 'Value must not be null or undefined' );
    requireNonNil( suffix, 'Suffix must not be null or undefined' );
    return value.endsWith( suffix ) ? value : value + suffix;
}

/** Splits a string by a separator and trims each resulting segment. */
export function splitTrimmed( value: string, separator: string | RegExp ): string[] {
    return value?.split( separator )?.map( trimToEmpty ) ?? [];
}

/** Joins an array of strings, filtering out any that are null, undefined, or blank, and trims the remaining strings. */
export function joinNonBlank( values: ( string | null | undefined )[], separator: string ): string {
    return values?.filter( v => !isBlank( v ) )
        ?.map( v => v!.trim() )
        ?.join( separator ) ?? '';
}

/** Normalizes line endings in a string to a specified format (default is '\n'). */
export function normalizeLineEndings( value: string, lineEnding: '\n' | '\r\n' = '\n' ): string {
    requireNonNil( value, 'Value must not be null or undefined' );
    return value.replace( /\r\n|\r|\n/g, lineEnding );
}

/** Escapes special characters in a string for use in a regular expression. */
export function escapeRegExp( value: string | null | undefined ): string | null | undefined {
    return isNil( value ) ? value : value.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
}

/**
 * Advanced string splitting function that respects nested structures, quotes, and escape characters.\
 * Supports the following options:
 * - `brackets`: An array of bracket pairs (e.g., `['()', '[]', '{}']`) that define nested structures to ignore when splitting.\
 *   While inside a bracketed section, the separator will be ignored. Tracks nested depth.
 * - `quotes`: An array of quote characters (e.g., `['"', "'"]`) that define quoted sections to ignore when splitting.\
 *   While inside a quoted section, all characters will be ignored until the same quote character is encountered again. (Unless it is escaped)
 * - `escapeChars`: An array of characters that can escape the next character, allowing separators, brackets, or quotes to be treated as literal characters.
 * - `trimSegments`: If true, trims whitespace from each resulting segment.
 * @param value The string to split.
 * @param separator The character to split the string on. (Defaults to `,`)
 * @param options Optional settings for handling brackets, quotes, escape characters, and trimming.
 * @returns An array of string segments resulting from the split operation, or an empty Array for blank strings.
 * @throws Error if the separator is not a single character, or if any escape characters, quotes, or brackets are not properly defined as chars or char pairs.
 */
export function splitX( value: string, separator: string = ',', options?: SplitXOptions ): string[] {
    requireTrue( separator.length === 1, 'Separator must be a single character' );
    if ( options ) {
        requireTrue( options.escapeChars?.every( char => char.length === 1 ) ?? true, 'All escape characters must be a single character' );
        requireTrue( options.quotes?.every( char => char.length === 1 ) ?? true, 'All quote characters must be a single character' );
        requireTrue( options.brackets?.every( pair => pair.length === 2 ) ?? true, 'All bracket pairs must consist of exactly two characters' );
    }

    if ( isBlank( value ) ) {
        return [];
    }

    const openingBrackets = new Set( options?.brackets?.map( b => b[0] ) ?? [] );
    const closingBrackets = new Set( options?.brackets?.map( b => b[1] ) ?? [] );
    const quoteChars = new Set( options?.quotes ?? [] );
    const escapeChars = new Set( options?.escapeChars ?? [] );
    const trimSegments = options?.trimSegments ?? false;

    const result: string[] = [];
    let segmentStart = 0;
    let depth = 0;
    let openQuote: string | null = null;

    for ( let i = 0; i < value.length; i++ ) {
        const char = value[i];

        if ( escapeChars.has( char ) ) {
            i++; // skip escaped character, do not inspect it
            continue;
        }

        if ( openQuote !== null ) {
            if ( char === openQuote ) {
                openQuote = null;
            }
            continue;
        }

        if ( quoteChars.has( char ) ) {
            openQuote = char;
            continue;
        }

        if ( openingBrackets.has( char ) ) {
            depth++;
            continue;
        }

        if ( closingBrackets.has( char ) ) {
            depth--;
            continue;
        }

        if ( char === separator && depth === 0 ) {
            const segment = value.substring( segmentStart, i );
            result.push( trimSegments ? segment.trim() : segment );
            segmentStart = i + 1;
        }
    }

    const lastSegment = value.substring( segmentStart );
    result.push( trimSegments ? lastSegment.trim() : lastSegment );
    return result;
}